/**
 * Registry of the route segments that gate behind the shared `EntitySelector`
 * and therefore own a per-segment `entityOverride` scope.
 *
 * This is the single source of truth for two things:
 *   1. Which route prefixes "own" an entity-scope selection — consumed by
 *      `matchScopedSegment` / `useEntityScopeReset` to clear the override when
 *      navigation leaves a segment (legacy clear-on-unmount parity; see
 *      `docs/migration/view-as-scope/link-graph.md`).
 *   2. The per-segment selector config (`allowedEntities` / `showAdminOption`)
 *      mirrored verbatim from the legacy page wrappers (`portalnew`
 *      `pages/{Integrations,Exports,Users,Webhooks,Routing,Catalogs}/index.js`).
 *      Route files import these so the picker config can't drift per segment.
 *
 * Account Info is intentionally absent — it is a Merchants/Vendors list tab
 * switcher, not an entity-scope gate, and legacy never wrapped it in
 * `EntitySelector`.
 */

export interface ScopedSegment {
  /** Route prefix that owns the scope, e.g. `/admin/plugins`. */
  prefix: string;
  /** Entity tabs the picker offers on this segment. */
  allowedEntities: Array<'merchant' | 'vendor'>;
  /** Whether the super-admin "Continue as Admin" banner shows on this segment. */
  showAdminOption: boolean;
}

export const SCOPED_SEGMENTS: readonly ScopedSegment[] = [
  { prefix: '/admin/plugins', allowedEntities: ['merchant', 'vendor'], showAdminOption: true },
  { prefix: '/admin/exports', allowedEntities: ['merchant', 'vendor'], showAdminOption: true },
  { prefix: '/admin/users', allowedEntities: ['merchant', 'vendor'], showAdminOption: true },
  { prefix: '/admin/webhooks', allowedEntities: ['merchant', 'vendor'], showAdminOption: false },
  { prefix: '/admin/routing', allowedEntities: ['merchant'], showAdminOption: false },
  { prefix: '/admin/catalogs', allowedEntities: ['merchant'], showAdminOption: false },
  { prefix: '/admin/products', allowedEntities: ['vendor'], showAdminOption: false },
];

/**
 * History-state marker carried by main-menu (sidebar) navigations. A sidebar
 * click must always land the user back on the merchant/vendor picker — even a
 * re-click of the section they're already scoped into — so the
 * Sidebar stamps every nav Link with this and `useEntityScopeReset` clears the
 * manual override when a navigation carrying it arrives. It lives in history
 * `state` so OTHER pushes to a segment root (Cancel / back-to-list buttons,
 * `EntitySelector`'s own picked-content entry) are untouched and keep the
 * scope.
 */
export const RESET_SCOPE_STATE = '__resetEntityScope';

/**
 * History-state key carrying an entity scope chosen on another surface (e.g.
 * the Home dashboard's onboarding steps) into a gated segment. `EntitySelector`
 * adopts it on arrival so the destination lands pre-scoped and skips the
 * picker. It must be applied by the DESTINATION (where the location already
 * matches the owning segment) — an origin that set the override before
 * navigating would trip `useEntityScopeReset`, which sees the not-yet-current
 * segment as "already left" and drops the scope mid-transition.
 */
export const SEEDED_ENTITY_SCOPE_STATE = '__seedEntityScope';

/** Entity handed off via {@link SEEDED_ENTITY_SCOPE_STATE}. No `prefix`/`segment`
 * — `EntitySelector` stamps those from the segment it adopts the seed on. */
export interface SeededEntityScope {
  type: 'merchant' | 'vendor';
  id: string;
  name?: string;
  isParent?: boolean;
}

/** Extract a valid {@link SeededEntityScope} from a location's history state, or
 * null when absent / malformed / not allowed on the given segment. */
export function readSeededEntityScope(
  state: unknown,
  allowedEntities: ReadonlyArray<'merchant' | 'vendor'>,
): SeededEntityScope | null {
  const seed = (state as Record<string, unknown> | null)?.[SEEDED_ENTITY_SCOPE_STATE];
  if (!seed || typeof seed !== 'object') return null;
  const s = seed as Record<string, unknown>;
  if ((s.type !== 'merchant' && s.type !== 'vendor') || typeof s.id !== 'string') return null;
  if (!allowedEntities.includes(s.type)) return null;
  return {
    type: s.type,
    id: s.id,
    name: typeof s.name === 'string' ? s.name : undefined,
    isParent: s.type === 'merchant' && s.isParent === true,
  };
}

/** True when `pathname` is the prefix itself or a sub-route of it (segment
 * boundary required so `/admin/plugins` doesn't match `/admin/plugins-foo`). */
export function isWithinSegment(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** The scoped segment that owns `pathname`, or null on a non-scoped page. */
export function matchScopedSegment(pathname: string): ScopedSegment | null {
  return SCOPED_SEGMENTS.find((s) => isWithinSegment(pathname, s.prefix)) ?? null;
}
